import { Container, Graphics, type FederatedPointerEvent } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { t, type I18nKey } from '@/i18n';
import type { RunStats } from '@/core/records';
import { sfx } from '@/audio/sfx';
import {
  SAC_H, SAC_W, buildDeviceHint, buildRunSummary, buildSacrificeCard, type SacrificeItem,
} from './gameover-cards';
import { buildStatsPanelViews, type StatView } from './stats-panel';
import { chamferPanel, label, targetBrackets } from './widgets';

export interface GameOverCallbacks {
  /**
   * Apply the burn choices and respawn (docs/12 C4).
   * @param burnedOffers card-offer indices in burn order
   * @param weaponBurned the weapon candidate was burned (F3)
   * @param extraArmor armor units earned by burns past the tax
   * @param toMenu true → save the checkpoint and return to the title
   */
  resolve(burnedOffers: number[], weaponBurned: boolean, extraArmor: number, toMenu: boolean): void;
}

export interface GameOverData {
  stats: RunStats;
  improved: ReadonlyArray<keyof RunStats>;
  seed: number;
  totalRuns: number;
  /** Mandatory burns (collection-scaled, docs/12 C4) */
  tax: number;
  items: SacrificeItem[];
  /** Ship stats with these card offer indices removed (removal preview) */
  statsView(excludedOffers: ReadonlyArray<number>): StatView;
}

/** Fixed design space — scaled to fill the real canvas (no overlap drift). */
const DESIGN_W = 1280;
const DESIGN_H = 720;
const BURN_TIME = 0.55;
/** Full-size burn cards (polish 1): a narrower stats column makes room */
const SUMMARY_X = 36;
const SUMMARY_W = 300;
const GRID_X = 356;
const GRID_Y = 112;
const GRID_GAP = 12;
const BURN_HEAD_Y = 82;
const STATUS_X = 1000;

/**
 * Game over — broken-red theme. Burn the mandatory tax from real card
 * visuals, optionally burn more for armor, watch the loss in the ship
 * status panel, then fly again or head back to the main menu.
 */
export class GameOverScreen {
  readonly view = new Container();
  private readonly dim = new Graphics();
  private readonly layer = new Container();
  open = false;
  private selected = 0;
  private data: GameOverData | null = null;
  private burned: boolean[] = [];
  private burnOrder: number[] = [];
  private burning: { itemIndex: number; node: Container; t: number } | null = null;

  constructor(private readonly callbacks: GameOverCallbacks) {
    this.view.visible = false;
    this.view.addChild(this.dim, this.layer);
  }

  show(screenW: number, screenH: number, data: GameOverData): void {
    this.open = true;
    this.view.visible = true;
    this.data = data;
    this.burned = data.items.map(() => false);
    this.burnOrder = [];
    this.burning = null;
    this.selected = 0;
    this.resize(screenW, screenH);
    this.rebuild();
  }

  /** Re-fit dim + design-space scale (fullscreen toggles, window resizes). */
  resize(screenW: number, screenH: number): void {
    this.dim.clear().rect(0, 0, screenW, screenH).fill({ color: 0x1a0508, alpha: 0.93 });
    const scale = Math.min(screenW / DESIGN_W, screenH / DESIGN_H);
    this.layer.scale.set(scale);
    this.layer.position.set(
      (screenW - DESIGN_W * scale) / 2,
      (screenH - DESIGN_H * scale) / 2,
    );
  }

  // ---- selection model: unburned items, then CONTINUE, then MENU ----

  private get slots(): number[] {
    const live: number[] = [];
    this.data?.items.forEach((_, i) => {
      if (!this.burned[i]) live.push(i);
    });
    return [...live, -1, -2]; // -1 = continue, -2 = menu
  }

  private get burnedCount(): number {
    return this.burnOrder.length;
  }

  private get taxPaid(): boolean {
    return this.data !== null && this.burnedCount >= Math.min(this.data.tax, this.data.items.length);
  }

  /** Armor from burns past the tax, in burn order. */
  private get extraArmor(): number {
    const data = this.data;
    if (!data) return 0;
    return this.burnOrder
      .slice(Math.min(data.tax, data.items.length))
      .reduce((sum, i) => sum + data.items[i]!.armorBonus, 0);
  }

  private rebuild(): void {
    const data = this.data;
    if (!data) return;
    this.layer.removeChildren().forEach((c) => c.destroy({ children: true }));

    const title = label(t('gameover.title'), 40, COLORS.dangerRed, 6);
    title.anchor.set(0.5, 0);
    title.position.set(DESIGN_W / 2, 18);
    this.layer.addChild(title);
    this.layer.addChild(buildRunSummary(
      data.stats, data.improved, data.seed, data.totalRuns, SUMMARY_X, SUMMARY_W,
    ));
    this.buildBurnArea(data);
    this.buildStatus(data);
    this.buildButtons();
    this.layer.addChild(buildDeviceHint(DESIGN_W / 2, DESIGN_H - 10));
  }

  private buildBurnArea(data: GameOverData): void {
    if (data.items.length === 0) return;
    const tax = Math.min(data.tax, data.items.length);
    const left = Math.max(0, tax - this.burnedCount);
    const head = label(
      left > 0 ? t('gameover.burnTitle', { n: left }) : t('gameover.extraHint'),
      17, left > 0 ? 0xffffff : COLORS.hudArmor, 1,
    );
    head.anchor.set(0.5, 0);
    head.position.set(GRID_X + (SAC_W * 3 + GRID_GAP * 2) / 2, BURN_HEAD_Y);
    this.layer.addChild(head);

    const slots = this.slots;
    data.items.forEach((item, i) => {
      const col = i % 3;
      const row = Math.floor(i / 3);
      const node = buildSacrificeCard(
        item,
        slots[this.selected] === i,
        this.burned[i]!,
        this.taxPaid && !this.burned[i],
      );
      node.position.set(GRID_X + col * (SAC_W + GRID_GAP), GRID_Y + row * (SAC_H + GRID_GAP));
      if (!this.burned[i]) {
        node.eventMode = 'static';
        node.cursor = 'pointer';
        // Mouse-only hover (touch: the tap below selects + burns in one go)
        node.on('pointerover', (e: FederatedPointerEvent) => { if (e.pointerType === 'mouse') this.selectItem(i); });
        node.on('pointertap', () => {
          this.selectItem(i);
          this.burnSelected();
        });
      }
      this.layer.addChild(node);
      if (this.burning?.itemIndex === i) this.burning.node = node;
    });
  }

  private buildStatus(data: GameOverData): void {
    const current = data.statsView(this.burnedCardOffers());
    const sel = this.slots[this.selected]!;
    let next: StatView | null = null;
    if (sel >= 0 && data.items[sel]!.kind === 'card') {
      next = data.statsView([...this.burnedCardOffers(), data.items[sel]!.offerIndex]);
    }
    const panel = buildStatsPanelViews(250, 470, current, next);
    panel.position.set(STATUS_X, 116);
    this.layer.addChild(panel);
  }

  private buildButtons(): void {
    const defs = [
      { slot: -1, key: 'gameover.continueBtn' as I18nKey, w: 290, x: 510 },
      { slot: -2, key: 'gameover.menuBtn' as I18nKey, w: 240, x: 830 },
    ];
    for (const d of defs) {
      const selected = this.slots[this.selected] === d.slot;
      const enabled = this.taxPaid;
      const btn = new Container();
      const bg = new Graphics();
      chamferPanel(bg, d.w, 52, {
        fill: selected ? 0x2a2440 : 0x161222,
        stroke: selected ? COLORS.uiAccent : enabled ? 0x4a4256 : 0x2c2738,
        strokeWidth: selected ? 4 : 3,
        notch: 12,
      });
      if (selected) targetBrackets(bg, d.w, 52);
      const txt = label(t(d.key), 19, enabled ? (selected ? COLORS.playerYellow : 0xffffff) : 0x4a4f58, 2);
      txt.anchor.set(0.5);
      txt.position.set(d.w / 2, 26);
      btn.addChild(bg, txt);
      btn.position.set(d.x, 642);
      btn.eventMode = 'static';
      btn.cursor = enabled ? 'pointer' : 'default';
      const slotIndex = this.slots.indexOf(d.slot);
      btn.on('pointerover', (e: FederatedPointerEvent) => { if (e.pointerType === 'mouse') this.select(slotIndex); });
      btn.on('pointertap', () => {
        this.select(slotIndex);
        this.activateSelected();
      });
      this.layer.addChild(btn);
    }
  }

  private burnedCardOffers(): number[] {
    const data = this.data;
    if (!data) return [];
    return this.burnOrder
      .filter((i) => data.items[i]!.kind === 'card')
      .map((i) => data.items[i]!.offerIndex);
  }

  private select(i: number): void {
    if (i !== this.selected && i >= 0) {
      this.selected = i;
      this.rebuild();
    }
  }

  private selectItem(itemIndex: number): void {
    this.select(this.slots.indexOf(itemIndex));
  }

  moveSelection(dir: number): void {
    if (this.burning) return;
    this.selected = (this.selected + dir + this.slots.length) % this.slots.length;
    sfx.uiMove();
    this.rebuild();
  }

  activateSelected(): void {
    if (this.burning) return;
    const slot = this.slots[this.selected];
    if (slot === undefined) return;
    if (slot >= 0) {
      this.burnSelected();
      return;
    }
    if (!this.taxPaid) return;
    sfx.uiConfirm();
    this.callbacks.resolve(
      this.burnedCardOffers(),
      this.burnOrder.some((i) => this.data!.items[i]!.kind === 'weapon'),
      this.extraArmor,
      slot === -2,
    );
  }

  /** Start the crack/char animation; the model flips when it finishes. */
  private burnSelected(): void {
    const slot = this.slots[this.selected]!;
    if (slot < 0 || this.burning) return;
    sfx.playerHit();
    this.burning = { itemIndex: slot, node: this.layer, t: 0 };
    this.rebuild(); // rebuild captures the node reference for the animation
  }

  /** Drives the burn animation — called every logic tick while open. */
  update(dt: number): void {
    const burn = this.burning;
    if (!burn) return;
    burn.t += dt;
    const p = Math.min(1, burn.t / BURN_TIME);
    burn.node.pivot.set(SAC_W / 2, SAC_H / 2);
    burn.node.x = GRID_X + (burn.itemIndex % 3) * (SAC_W + GRID_GAP) + SAC_W / 2
      + Math.sin(burn.t * 55) * 5 * (1 - p);
    burn.node.y = GRID_Y + Math.floor(burn.itemIndex / 3) * (SAC_H + GRID_GAP) + SAC_H / 2;
    burn.node.rotation = Math.sin(burn.t * 40) * 0.05 * (1 - p);
    burn.node.alpha = 1 - p * 0.5;
    if (p >= 1) {
      this.burned[burn.itemIndex] = true;
      this.burnOrder.push(burn.itemIndex);
      this.burning = null;
      if (this.selected >= this.slots.length) this.selected = this.slots.length - 1;
      this.rebuild();
    }
  }

  hide(): void {
    this.open = false;
    this.view.visible = false;
    this.burning = null;
  }
}
